// THE SECOND STEP (STEP-UP-POLICY.md), as a form.
//
// A person reaches this because their password was RIGHT and the sign-in was
// refused anyway. That is a confusing moment, so the screen says the reason the
// mail says, offers the code entry, and offers nothing else — no "try a
// different account", no password field to re-fill, nothing that invites them to
// conclude they typed something wrong.
//
// MODULE-OWNED (Law F1). All three portals mount it from a depth-1 login screen;
// the alternative was three copies of a security-critical form, and three copies
// is where one of them quietly stops sending `rememberDevice`.
//
// ONE INPUT, NOT SIX BOXES — the same reasoning as ../otp: a segmented control
// fights password managers and breaks the platform's one-time-code autofill.
import { Show, createSignal } from "solid-js";
import { Button } from "@erp/ui/button";
import { Checkbox } from "@erp/ui/checkbox";
import { Field, TextInput } from "@erp/ui/field";
import { createTurnstile } from "@erp/ui/turnstile";
import { KeyRound } from "lucide-solid";

/** The API's one refusal sentence, echoed when the network ate the response. */
const GENERIC = "That code is not valid. Ask for a new one by signing in again.";

export interface StepUpFormProps {
  /** The address the password belonged to — never editable here. */
  email: string;
  /**
   * Why the code was sent, in the server's own words when it gave them. The
   * template promises a specific reason and this screen must not contradict it.
   */
  reason?: string;
  /** Called once the code is spent and the session cookie is set. */
  onVerified: () => void | Promise<void>;
  /** Abandon the step-up and go back to the password form. */
  onCancel?: () => void;
}

export function StepUpForm(props: StepUpFormProps) {
  const [code, setCode] = createSignal("");
  const [remember, setRemember] = createSignal(false);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");
  // Public by necessity — the caller has no session yet, being refused one is
  // why they are here — so this form carries the human check like every other.
  const captcha = createTurnstile({ action: "step-up" });

  const submit = async (e: Event) => {
    e.preventDefault();
    if (busy() || code().trim().length < 4) return;
    setBusy(true);
    setError("");
    let result: { ok: boolean; message?: string };
    try {
      const res = await fetch("/api/auth/step-up/verify", {
        method: "POST",
        // The nonce cookie is what binds this code to this browser, and the
        // session cookie comes back on the response — both need credentials.
        credentials: "include",
        headers: { "content-type": "application/json", ...captcha.headers() },
        body: JSON.stringify({ email: props.email, code: code().trim(), rememberDevice: remember() }),
      });
      result = (await res.json()) as { ok: boolean; message?: string };
    } catch {
      result = { ok: false, message: "Could not reach the server. Check your connection and try again." };
    }
    // Single-use, spent on every attempt (see the login screens).
    captcha.reset();
    setBusy(false);
    if (result.ok) {
      await props.onVerified();
      return;
    }
    setError(result.message ?? GENERIC);
    // Clear the field: the next attempt is a fresh code out of the same mail,
    // and leaving a wrong one in place invites re-submitting it.
    setCode("");
  };

  return (
    <form id="form-step-up" class="mt-6 flex flex-col gap-2" onSubmit={(e) => void submit(e)}>
      <p class="text-sm text-base-content">
        We sent a verification code to <span class="font-bold">{props.email}</span>.
      </p>
      <Show when={props.reason}>
        <p class="text-detail-s text-base-subtle">{props.reason}</p>
      </Show>
      <Field
        label="Verification code"
        required
        hint="It expires in ten minutes. Codes are single-use."
        error={error() || undefined}
      >
        <TextInput
          id="input-step-up-code"
          aria-label="Verification code"
          inputmode="numeric"
          autocomplete="one-time-code"
          pattern="[0-9]*"
          maxlength={6}
          autofocus
          required
          placeholder="123456"
          value={code()}
          onInput={(e) => {
            setCode(e.currentTarget.value.replace(/\D/g, ""));
            if (error()) setError("");
          }}
        />
      </Field>
      {/* OPT-IN, never pre-ticked. Ticking it is a 30-day decision about a
          shared or borrowed machine, and a default-on box is how that decision
          gets made by accident. */}
      <Checkbox
        id="input-step-up-remember"
        class="mt-1"
        checked={remember()}
        onChange={setRemember}
        label="Remember this device for 30 days"
      />
      <captcha.Widget class="mt-1" />
      <Button
        id="btn-step-up-submit"
        type="submit"
        variant="primary"
        loading={busy()}
        disabled={code().trim().length < 4 || !captcha.ready()}
        class="mt-2 w-full"
      >
        <KeyRound size={15} /> Verify and sign in
      </Button>
      <Show when={props.onCancel}>
        <button
          id="btn-step-up-cancel"
          type="button"
          class="text-detail-s text-base-subtle underline"
          onClick={() => props.onCancel?.()}
        >
          Use a different account
        </button>
      </Show>
      <p class="mt-2 text-detail-s text-base-subtle">
        Our staff will never ask you for this code — not by phone, not by email, not in person.
      </p>
    </form>
  );
}
